import type { TFunction } from "i18next";
import { formatDateTime } from "../../../lib/datetime/displayDateTime";

import Chip from "@mui/material/Chip";
import { optionLabel } from "../../../lib/i18n/optionLabel";

const planStatusChipColor = (status?: string) => {
  if (status === "ACTIVE") return "success" as const;
  if (status === "PASSIVE") return "warning" as const;
  return "default" as const;
};

const formatQuota = (t: TFunction, value?: number) =>
  value === -1 ? t("common.unlimited") : String(value ?? 0);

export const getPlanCols = (t: TFunction) => [
  {
    name: t("plans.cols.name"),
    selector: (row: any) => row.name,
    sortable: true,
  },
  {
    name: t("plans.cols.description"),
    selector: (row: any) => row.description || "-",
    sortable: false,
    wrap: true,
  },
  {
    name: t("plans.cols.status"),
    selector: (row: any) => row.status,
    sortable: true,
    cell: (row: any) => {
      const c = planStatusChipColor(row.status);
      return (
        <Chip
          label={optionLabel(t, "options.entityStatus", row.status)}
          size="small"
          color={c}
          variant={c === "default" ? "outlined" : "filled"}
        />
      );
    },
  },
  {
    name: t("plans.cols.chatLimits"),
    selector: (row: any) =>
      `${formatQuota(t, row.chatMessageLimits?.dailyLimit)}/${formatQuota(t, row.chatMessageLimits?.weeklyLimit)}/${formatQuota(t, row.chatMessageLimits?.monthlyLimit)}`,
    sortable: false,
  },
  {
    name: t("plans.cols.mailLimits"),
    selector: (row: any) =>
      `${formatQuota(t, row.mailAnalysisLimits?.dailyLimit)}/${formatQuota(t, row.mailAnalysisLimits?.weeklyLimit)}/${formatQuota(t, row.mailAnalysisLimits?.monthlyLimit)}`,
    sortable: false,
  },
  {
    name: t("plans.cols.createdDate"),
    selector: (row: any) =>
      row.createdDate ? formatDateTime(row.createdDate) : "-",
    sortable: true,
  },
];

const PlansModel = {
  get cols() {
    return getPlanCols(((key: string) => key) as TFunction);
  },
  createDialogOpts: (t: TFunction) => ({
    submitButtonText: t("common.create"),
    cancelButtonText: t("common.cancel"),
    title: t("plans.createTitle"),
    content: t("plans.createContent"),
  }),
  editDialogOpts: (t: TFunction) => ({
    submitButtonText: t("common.update"),
    cancelButtonText: t("common.cancel"),
    title: t("plans.editTitle"),
    content: t("plans.editContent"),
  }),
  WARNING: {
    color: "warning",
    icon: "warning",
    title: "",
    content: "",
    info: "",
  },
  ERROR: {
    color: "error",
    icon: "error",
    title: "",
    content: "",
    info: "",
  },
  SUCCESS: {
    color: "success",
    icon: "success",
    title: "",
    content: "",
    info: "",
  },
  async message(type: any, title: any, content: any, info: any) {
    return new Promise((resolve) => {
      type.title = title;
      type.content = content;
      type.info = info;
      resolve(type);
    });
  },
};

export default PlansModel;
